import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ROUNDS = [
  {
    text: "Hello, world!",
    tokens: 4,
    hint: "Punctuation and short words each count.",
  },
  {
    text: "The quick brown fox jumps over the lazy dog.",
    tokens: 10,
    hint: "Common short words tokenize efficiently — usually 1 word ≈ 1 token.",
  },
  {
    text: "Retrieval-Augmented Generation",
    tokens: 5,
    hint: "Hyphenated compound words split at the hyphen.",
  },
  {
    text: "import { useState, useEffect } from 'react';",
    tokens: 14,
    hint: "Code is token-hungry — every symbol ( { , } ) ' ; is often its own token.",
  },
  {
    text: "Rishi built a Knowledge Graph RAG system using LangGraph and Neo4j that reduced query latency by 50%.",
    tokens: 22,
    hint: "Technical nouns like 'LangGraph' and 'Neo4j' often split into 2–3 subword tokens.",
  },
];

function scoreGuess(guess, real) {
  const diff = Math.abs(guess - real);
  if (diff === 0) return 3;
  if (diff <= 2) return 2;
  if (diff <= 5) return 1;
  return 0;
}

export default function TokenEstimator({ onDone }) {
  const [idx, setIdx]       = useState(0);
  const [input, setInput]   = useState("");
  const [score, setScore]   = useState(0);
  const [result, setResult] = useState(null);

  const round = ROUNDS[idx];

  const submit = () => {
    const guess = parseInt(input, 10);
    if (isNaN(guess) || guess < 1) return;
    const pts = scoreGuess(guess, round.tokens);
    setScore(s => s + pts);
    setResult({ guess, pts });
  };

  const next = () => {
    if (idx + 1 >= ROUNDS.length) { onDone(score); return; }
    setIdx(i => i + 1);
    setInput("");
    setResult(null);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, color: "var(--text-muted)", fontSize: "0.8rem" }}>
        <span>Round {idx + 1}/{ROUNDS.length}</span>
        <span>Score: {score} pts</span>
      </div>
      <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: 20 }}>
        ±0 = 3 pts · ±1–2 = 2 pts · ±3–5 = 1 pt
      </p>

      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
          <div style={{
            background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 12,
            padding: 20, marginBottom: 20, fontFamily: "monospace",
            fontSize: "0.9rem", color: "var(--text)", lineHeight: 1.6,
          }}>
            {round.text}
          </div>

          {!result ? (
            <div style={{ display: "flex", gap: 10 }}>
              <input
                type="number" min="1" max="999" value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && submit()}
                placeholder="Your token count"
                style={{
                  flex: 1, height: 44, padding: "0 14px", borderRadius: 10, fontSize: "16px",
                  border: "1px solid var(--border)", background: "var(--bg)", color: "var(--text)",
                  outline: "none",
                }}
              />
              <button className="btn-pill" onClick={submit} style={{ whiteSpace: "nowrap" }}>Guess</button>
            </div>
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div style={{
                padding: "16px 20px", borderRadius: 12, marginBottom: 12, fontSize: "0.875rem",
                background: result.pts === 3 ? "rgba(34,197,94,0.1)" : result.pts > 0 ? "rgba(251,191,36,0.1)" : "rgba(239,68,68,0.1)",
                border: `1px solid ${result.pts === 3 ? "rgba(34,197,94,0.3)" : result.pts > 0 ? "rgba(251,191,36,0.3)" : "rgba(239,68,68,0.3)"}`,
                color: "var(--text)",
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {result.pts === 3 ? "Exact. " : result.pts === 2 ? "Close. " : result.pts === 1 ? "Near. " : "Off. "}
                  Your guess: <strong>{result.guess}</strong> · Actual: <strong>{round.tokens}</strong> · +{result.pts} pts
                </div>
                <div style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>{round.hint}</div>
              </div>
              <button className="btn-pill" onClick={next} style={{ width: "100%", justifyContent: "center" }}>
                {idx + 1 >= ROUNDS.length ? "See Results" : "Next →"}
              </button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
